import { useEffect, useRef, useState, type KeyboardEvent, useId } from "react";
import "./tableTextFilter.css";

interface TableTextFilterProps {
	clear: () => void;
	onSubmit: (value: string) => void;
	placeholder: string;
	title?: string;
	number?: boolean;
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
}

const sanitizeTextFilterValue = (rawValue: string): string =>
	rawValue.replace(/[^a-zA-ZÀ-ž0-9\s\-–_.@'’]+/g, "").replace(/^\s+/, "");

/**
 * Uncontrolled while typing — Chromium doubles characters (e.g. `'`) when `input.value`
 * is assigned during an input event (React controlled inputs do that).
 *
 * @see https://github.com/microsoft/fluentui/issues/19054
 */
export function TableTextFilter({ clear, onSubmit, onChange, value, placeholder, title, number = false, disabled = false }: TableTextFilterProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [showClearButton, setShowClearButton] = useState(value !== "");
	const id = useId();

	useEffect(() => {
		const input = inputRef.current;
		if (input === null) {
			return;
		}
		if (value === "" && input.value !== "") {
			input.value = "";
		}
		if (value !== "" && input.value !== value) {
			// External apply / restore — not during an input event, so safe to write.
			input.value = value;
		}
		setShowClearButton(value !== "" || input.value !== "");
	}, [value]);

	const sanitizeForMode = (rawValue: string): string => {
		if (number) {
			let newValue = rawValue.replace(/[^0-9-]/g, "");
			if (newValue.indexOf("-") !== newValue.lastIndexOf("-")) {
				newValue = newValue.replace(/-/g, "");
			}
			if (newValue.indexOf("-") > 0) {
				newValue = newValue.replace(/-/g, "");
			}
			return newValue;
		}

		return sanitizeTextFilterValue(rawValue);
	};

	const handleNativeInput = () => {
		const input = inputRef.current;
		if (input === null) {
			return;
		}
		// Local UI only — do not call parent onChange (that applies the datagrid filter).
		setShowClearButton(input.value !== "");
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (disabled) {
			return;
		}

		if (event.key !== "Enter") {
			return;
		}

		event.preventDefault();

		const input = inputRef.current;
		const rawValue = input?.value ?? "";
		const nextValue = sanitizeForMode(rawValue);

		if (input !== null && input.value !== nextValue) {
			input.value = nextValue;
		}
		setShowClearButton(nextValue !== "");
		onChange(nextValue);

		if (nextValue === "") {
			clear();
			return;
		}

		onSubmit(nextValue);
	};

	const handleClear = () => {
		if (disabled) {
			return;
		}

		const input = inputRef.current;
		if (input !== null) {
			input.value = "";
		}
		setShowClearButton(false);
		onChange("");
		clear();
		input?.focus();
	};

	return (
		<div className="searchInput-container">
			<input
				ref={inputRef}
				id={id}
				className="searchField"
				placeholder={placeholder}
				title={title}
				type="text"
				pattern={number ? "\\d*" : undefined}
				defaultValue={value}
				autoComplete="off"
				autoCorrect="off"
				autoCapitalize="off"
				spellCheck={false}
				disabled={disabled}
				onInput={handleNativeInput}
				onKeyDown={handleKeyDown}
			/>
			<div
				className={`clearButton ${showClearButton ? "" : "inactive"} ${disabled ? "inactive" : ""}`}
				onClick={disabled ? undefined : handleClear}
			>
				<span className="asideListSearch__clear-icon icon icon-light-cross icon icon-grey icon-new icon-12" />
			</div>
		</div>
	);
}
